import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { ComponentLayout } from '../../components/layouts/ComponentLayout'
import { BodyLargeMedium, BodySmall, BodySmallMedium, Title2 } from '../../components/styles/Typography'
import { Arrow, InvoiceBlank, User } from '../../components/icons'
import { theme } from '../../theme/theme'
import { StackedBarChart } from '../../components/charts/StackedBarChart'
import { useGetServicePages } from '../../api/contentful/contentfulQueries'
import { TrainingId, TrackingEvents } from '@estercare/ester-shared'
import { Link } from 'react-router-dom'
import * as IconComponents from '../../components/icons'
import React from 'react'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { ServiceCard } from '../../components/ServiceCard'
import { useSelectActiveServicesFromContentful, useSelectUserOrganisationId } from '../../state/selectors'
import { getTrainingData } from '../../utils/statistics/chartData'
import { getServiceLink } from '../../utils/services'
import { useGetUsersFromOrganisationQuery } from '../../api/ester-b2b-api/private/users/userQueries'
import { useGetTrainingStatistics } from '../../api/ester-b2b-api/private/trainings/trainingQueries'
import { Grid } from '../../components/Grid'
import { BlockItem } from '../../components/BlockItem'
import { useEmitEvent } from '../../hooks/analytics/useEmitEvent'
export const HomeAdmin = () => {
  const { t } = useTranslation()
  const emitEvent = useEmitEvent()
  const { data, isLoading: isLoadingServices } = useGetServicePages()

  const getStartedCardArr = [
    {
      icon: <User size={25} color={theme.color.white} />,
      largeText: t('admin.home.manageEmployeesTitle'),
      smallText: t('admin.home.manageEmployeesDescription'),
      link: 'employees',
    },
    {
      icon: <InvoiceBlank size={21} color={theme.color.white} />,
      largeText: t('admin.home.womensHealthPolicyTitle'),
      smallText: t('admin.home.womensHealthPolicyDescription'),
      link: 'services/policy',
    },
  ]
  const ServiceIndex = data?.items || []
  const activeServices = useSelectActiveServicesFromContentful(ServiceIndex, 'ADMIN')

  const organisationId = useSelectUserOrganisationId()
  const { data: womensHealthStatistics, isLoading: isWomensHealthStatisticsLoading } = useGetTrainingStatistics(
    TrainingId.WOMENS_HEALTH_TRAINING
  )
  const { data: employees, isLoading: isUsersLoading } = useGetUsersFromOrganisationQuery(organisationId)

  if (isWomensHealthStatisticsLoading || isUsersLoading || isLoadingServices) return <LoadingSpinner />

  const numberOfEmployees = employees?.length

  const handleNavigation = (action: string) => {
    emitEvent({
      eventType: TrackingEvents.NAVIGATE,
      action,
    })
  }

  const seeAllLink = ({ link, text }: { link: string; text: string }) => (
    <StyledLink to={link} onClick={() => handleNavigation(`see_all_${link.replace('/admin/', '')}`)}>
      <BodySmallMedium>{text}</BodySmallMedium>
      <Arrow size={16} />
    </StyledLink>
  )

  return (
    <>
      <ComponentLayout paddingBottom={0}>
        <StyledTitle2>{t('admin.home.title')}</StyledTitle2>
      </ComponentLayout>
      <ComponentLayout>
        <StyledBodyLargeMedium>{t('admin.home.gettingStartedTitle')}</StyledBodyLargeMedium>
        <Grid $columns={2}>
          {getStartedCardArr.map(({ largeText, icon, smallText, link }, index) => (
            <Link
              to={link}
              key={`link-${index}`}
              onClick={() => handleNavigation(`getting_started_${link.replace('/', '_')}`)}
            >
              <BlockItem
                key={index}
                backgroundColor={theme.color.plumMid}
                padding={theme.spacing.large}
                textColor={theme.color.white}
                height={200}
              >
                <GetStartedIconWrapper>{icon}</GetStartedIconWrapper>
                <CardTitle>{largeText}</CardTitle>
                <BodySmall>{smallText}</BodySmall>
              </BlockItem>
            </Link>
          ))}
        </Grid>
      </ComponentLayout>
      {activeServices.length > 0 && (
        <ComponentLayout paddingTop={0}>
          <TitleContainer>
            <BodyLargeMedium>{t('admin.home.servicesTitle')}</BodyLargeMedium>
            {seeAllLink({ link: '/admin/services', text: t('admin.home.servicesSeeAll') })}
          </TitleContainer>
          <Grid $columns={3}>
            {activeServices.map(
              ({ fields: { title, shortDescription, slug, iconType, serviceIdentifier }, sys: { id } }) => (
                <ServiceCard
                  key={id}
                  title={title!}
                  description={shortDescription || ''}
                  linkTo={getServiceLink(serviceIdentifier, slug)}
                  icon={
                    iconType &&
                    React.createElement(IconComponents[iconType as keyof typeof IconComponents], {
                      size: 22,
                      color: theme.color.plumMid,
                    })
                  }
                />
              )
            )}
          </Grid>
        </ComponentLayout>
      )}
      <ComponentLayout paddingTop={0}>
        <TitleContainer>
          <BodyLargeMedium>{t('admin.home.participationTitle')}</BodyLargeMedium>
          {seeAllLink({ link: '/admin/statistics', text: t('admin.home.participationSeeAll') })}
        </TitleContainer>
        {womensHealthStatistics && numberOfEmployees && (
          <StackedBarChart
            data={getTrainingData(womensHealthStatistics, numberOfEmployees, t)}
            title={t('admin.home.womensHealthTrainingTitle')}
          />
        )}
      </ComponentLayout>
    </>
  )
}

const StyledTitle2 = styled(Title2)`
  max-width: 400px;
`

const TitleContainer = styled.div`
  margin-bottom: ${theme.spacing.medium}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StyledBodyLargeMedium = styled(BodyLargeMedium)`
  margin-bottom: ${theme.spacing.medium}px;
`

const CardTitle = styled(BodyLargeMedium)`
  margin-bottom: ${theme.spacing.xsmall}px;
`
const GetStartedIconWrapper = styled.div`
  padding: 5px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${theme.color.white}15;
  margin-bottom: ${theme.spacing.medium}px;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${theme.color.plumMid};
  gap: 3px;
`
